// =============================================================================
// CLONE TEMPLATE UTILS
//
// Voice-Clone-mode templates are a fundamentally different paradigm from
// Standard-mode templates:
//
//   STANDARD MODE
//     - Pre-written text instructions (`topicSpecificNotes`)
//     - Subject line variants
//     - The AI writes around fixed copy
//
//   VOICE-CLONE MODE
//     - No pre-written text. Style comes 100% from a Voice Clone (Voice DNA).
//     - User defines WHERE the AI pulls content from (info sources)
//     - User pins campaign-level facts (fixed facts)
//     - User picks STRUCTURE VARIANTS to A/B test (e.g. Pain-First vs Curiosity)
//     - Each variant tracks its own reply rate so we know which structure wins
//
// Both modes share the same RTDB path, the same stats schema, the same email
// generation entry point — only the field set differs. The `mode` discriminator
// determines which editor opens and which generation pipeline runs.
// =============================================================================

export const TEMPLATE_MODES = {
  STANDARD: "standard",
  VOICE_CLONE: "voiceClone",
};

// =============================================================================
// BEAT META — every structural sub-section that can appear in a cold email,
// with its display name and color. Used to render the "blueprint" bar on
// each structure card and inside each variant.
//
// Colors are tied to the SEMANTIC role, not the structure — so e.g. a `pain`
// beat looks the same in Pain-First, PAS, and any other structure that
// includes one. This makes the visual language readable across structures.
// =============================================================================

export const BEAT_META = {
  // Pain / problem family — reds
  pain:       { label: "Pain",       color: "#ef4444" },
  agitate:    { label: "Agitate",    color: "#dc2626" },
  problem:    { label: "Problem",    color: "#ef4444" },
  amplify:    { label: "Amplify",    color: "#dc2626" },
  gap:        { label: "Gap",        color: "#ef4444" },

  // Relief / value / solve family — greens
  relief:     { label: "Relief",     color: "#10b981" },
  solve:      { label: "Solve",      color: "#10b981" },
  value:      { label: "Value",      color: "#10b981" },
  offer:      { label: "Offer",      color: "#10b981" },
  after:      { label: "After",      color: "#10b981" },
  transformation: { label: "Transformation", color: "#059669" },

  // Story / hook / curiosity family — ambers
  story:      { label: "Story",      color: "#f59e0b" },
  hook:       { label: "Hook",       color: "#f59e0b" },
  insight:    { label: "Insight",    color: "#d97706" },
  attention:  { label: "Attention",  color: "#f59e0b" },

  // Curiosity / proof / interest — purples
  punchline:  { label: "Punchline",  color: "#a855f7" },
  question:   { label: "Question",   color: "#a855f7" },
  proof:      { label: "Proof",      color: "#a855f7" },
  tease:      { label: "Tease",      color: "#a855f7" },
  testimony:  { label: "Testimony",  color: "#9333ea" },
  interest:   { label: "Interest",   color: "#a855f7" },

  // Connection / opener / desire — blues
  compliment: { label: "Compliment", color: "#3b82f6" },
  connect:    { label: "Connect",    color: "#6366f1" },
  ask:        { label: "Ask",        color: "#3b82f6" },
  relevance:  { label: "Relevance",  color: "#3b82f6" },
  desire:     { label: "Desire",     color: "#3b82f6" },
  permission: { label: "Permission", color: "#6366f1" },

  // Bridge / reframe — cyans
  bridge:     { label: "Bridge",     color: "#06b6d4" },
  reframe:    { label: "Reframe",    color: "#06b6d4" },

  // Context / framing — slates
  before:     { label: "Before",     color: "#94a3b8" },
  context:    { label: "Context",    color: "#64748b" },
  frame:      { label: "Frame",      color: "#6897b7" },

  // Action / response family — navy
  cta:        { label: "CTA",        color: "#1e3a8a" },
  action:     { label: "Action",     color: "#1e3a8a" },
  response:   { label: "Response",   color: "#1e3a8a" },
};

// =============================================================================
// STRUCTURE LIBRARY — proven cold-email frameworks the user can mix & match.
//
// IMPORTANT — Length is NOT independent from structure. Each structure has
// its own psychologically-correct base length (`baseWords`) and an internal
// rhythm of beats. PAS forced into 30 words doesn't work; Direct & Bold
// stretched to 200 words contradicts itself.
//
// The user picks a structure → its baseWords + beats are fixed. The only
// length knob is a tweak (shorter / natural / longer / mirror clone) that
// scales the whole thing while preserving the beat proportions.
//
// Each structure becomes a candidate variant; reply-rate is tracked per
// variant so we can see which structure × tweak wins.
// =============================================================================

export const STRUCTURE_LIBRARY = [
  // ========== BONUS STANDARD — the always-on default ==========
  // Curated 5-beat structure that ships pre-loaded as the first active
  // variant on every new Voice-Clone template. Sits at index 0 of the
  // library so the picker carousel opens centered on it. Combines a
  // relevance icebreaker with the classic problem→solve→proof→cta arc
  // — the BonusClub baseline that works across most cold-outreach
  // campaigns before the user starts A/B-testing alternatives.
  {
    id: "bonusStandard",
    label: "Bonus Standard",
    icon: "fa-gem",
    accent: "blue",
    summary:
      "Relevance icebreaker → problem → solve + outcome → proof → CTA. The default that just works.",
    structureHint:
      "Beat 1 — Open with a specific, relevance-driven icebreaker that proves you actually paid attention to this recipient (NOT generic small talk). Beat 2 — Name a real problem they likely have, in their own words. Beat 3 — Show how it could be solved AND the positive outcomes that follow (concrete consequence, not abstract benefit). Beat 4 — Demonstrate why YOU specifically are the right person/team to solve it (1 credibility moment, no humble-brag). Beat 5 — A clear, low-friction CTA so they know exactly what happens if they reply.",
    bestFor:
      "Any cold outreach where you want a proven, balanced default. The structure to start every campaign with.",
    psychology:
      "Five-beat arc combining attention (relevance hook), tension (named problem), release (solve + outcome), trust (proof) and direction (CTA). Each beat is the prerequisite for the next — skipping one breaks the chain. The relevance icebreaker is the only genuine differentiator vs. generic templates: it's why this email gets read.",
    // ~100 words is the BonusClub sweet-spot for the seeded default variant —
    // long enough to land all 5 beats with substance, short enough to stay
    // in the cold-open reply window. shorter/longer tweaks modulate around it.
    baseWords: 100,
    minWords: 75,
    maxWords: 170,
    beats: [
      { id: "icebreaker", weight: 2, hint: "Relevance-driven hook — show you noticed something specific about THEM. Not flattery, not weather." },
      { id: "problem",    weight: 2, hint: "Name the real problem they likely have. Their language, not yours." },
      { id: "solve",      weight: 3, hint: "How it could be solved + the positive consequences that follow. Concrete, not abstract." },
      { id: "proof",      weight: 2, hint: "Why YOU are the perfect partner to solve it — 1 credibility moment (result, name, number)." },
      { id: "cta",        weight: 1, hint: "Clear next step. No fluff. They should know exactly what happens if they reply." },
    ],
  },
  {
    id: "painFirst",
    label: "Pain-First",
    icon: "fa-bolt",
    accent: "red",
    summary: "Open with the pain → twist the knife → offer the relief.",
    structureHint:
      "Lead with a sharp, specific pain the recipient feels. One line of agitation. Then the relief — your solution in one breath. CTA.",
    bestFor: "Audiences that already know the problem.",
    psychology:
      "Loss-aversion bias: the brain weighs avoiding pain ~2× as heavily as pursuing gain. Naming a felt pain instantly engages — but only if it's specific enough to feel personally true.",
    baseWords: 70,
    minWords: 50,
    maxWords: 110,
    beats: [
      { id: "pain",    weight: 2, hint: "Sharp, specific pain in one breath." },
      { id: "agitate", weight: 1, hint: "One line that twists the knife." },
      { id: "relief",  weight: 2, hint: "The relief — your solution in one breath." },
      { id: "cta",     weight: 1, hint: "Direct ask. One question." },
    ],
  },
  {
    id: "curiosityHook",
    label: "Curiosity Hook",
    icon: "fa-question",
    accent: "purple",
    summary: "Surprising claim that opens a loop they need to close.",
    structureHint:
      "Open with a claim that sounds wrong or counterintuitive. Tease the resolution. Make them reply to find out the answer.",
    bestFor: "Cold audiences with high attention scarcity.",
    psychology:
      "Information-gap theory (Loewenstein): a known unknown creates discomfort the brain wants to resolve. Open the loop, withhold the close — the reply IS the resolution.",
    baseWords: 55,
    minWords: 35,
    maxWords: 90,
    beats: [
      { id: "hook", weight: 3, hint: "Counterintuitive / surprising claim." },
      { id: "tease", weight: 2, hint: "Open the loop. Withhold the answer." },
      { id: "cta",  weight: 1, hint: "Reply-to-find-out CTA." },
    ],
  },
  {
    id: "storyHook",
    label: "Story Hook",
    icon: "fa-book-open",
    accent: "amber",
    summary: "Tiny story that ties directly to their world.",
    structureHint:
      "Open with a 1-2 sentence story (real client, your own story). Tie the punchline directly to the recipient's situation.",
    bestFor: "Warm leads, founder-to-founder, personal angles.",
    psychology:
      "Stories bypass rational filters and activate mirror neurons — the recipient simulates the protagonist's experience. The punchline lands as their own realization, not your pitch.",
    baseWords: 95,
    minWords: 70,
    maxWords: 150,
    beats: [
      { id: "story",     weight: 4, hint: "1-2 sentence narrative." },
      { id: "punchline", weight: 2, hint: "Tie directly to the recipient." },
      { id: "cta",       weight: 1, hint: "Soft CTA." },
    ],
  },
  {
    id: "pas",
    label: "PAS — Problem · Agitate · Solve",
    icon: "fa-triangle-exclamation",
    accent: "red",
    summary: "Classic copywriting frame, slightly punchier than Pain-First.",
    structureHint:
      "Sentence 1: name the problem. Sentence 2: agitate it (cost / consequence). Sentence 3: introduce the solution as inevitable relief.",
    bestFor: "Offers with a clear measurable pain.",
    psychology:
      "Agitation widens the pain gap before the solve closes it — the relief feels more relieving relative to the agitation peak. Without the agitation step the solve lands flat.",
    baseWords: 75,
    minWords: 55,
    maxWords: 120,
    beats: [
      { id: "problem", weight: 1, hint: "Name the problem in one line." },
      { id: "agitate", weight: 1, hint: "Cost / consequence." },
      { id: "solve",   weight: 2, hint: "Solution as inevitable relief." },
      { id: "cta",     weight: 1, hint: "The ask." },
    ],
  },
  {
    id: "beforeAfterBridge",
    label: "Before · After · Bridge",
    icon: "fa-arrows-left-right",
    accent: "green",
    summary: "Show the gap between today and the better future.",
    structureHint:
      "Paint where they are now (1 line). Paint where they could be (1 line). The bridge = your offer (1 line). CTA = invitation to cross.",
    bestFor: "Transformation pitches, vision-driven offers.",
    psychology:
      "Contrast principle: the after-state feels bigger when the before-state is concrete. The bridge then positions the offer as the only logical step between them.",
    baseWords: 80,
    minWords: 60,
    maxWords: 130,
    beats: [
      { id: "before", weight: 2, hint: "Where they are now." },
      { id: "after",  weight: 2, hint: "Where they could be." },
      { id: "bridge", weight: 2, hint: "Your offer = the bridge." },
      { id: "cta",    weight: 1, hint: "Invitation to cross." },
    ],
  },
  {
    id: "complimentConnect",
    label: "Compliment · Connect",
    icon: "fa-handshake",
    accent: "blue",
    summary: "Specific genuine observation, then connect it to a value-add.",
    structureHint:
      "Open with a SPECIFIC observation about them (not generic flattery). Connect it to something you can help with.",
    bestFor: "Senior decision-makers, partnership-style outreach.",
    psychology:
      "Specific compliments signal you actually paid attention — generic flattery does the opposite and lowers trust. Specificity earns the right to ask for something.",
    baseWords: 85,
    minWords: 65,
    maxWords: 130,
    beats: [
      { id: "compliment", weight: 2, hint: "Specific, genuine observation." },
      { id: "connect",    weight: 2, hint: "Connect to your value-add." },
      { id: "cta",        weight: 1, hint: "Soft / light ask." },
    ],
  },
  {
    id: "questionLead",
    label: "Question Lead",
    icon: "fa-circle-question",
    accent: "purple",
    summary: "Open with a sharp question they actually want to answer.",
    structureHint:
      "First line is a high-stakes question relevant to their role/business. Brief context for why you're asking.",
    bestFor: "Research outreach, building relationships before pitching.",
    psychology:
      "Questions activate the brain's answer-seeking reflex faster than statements. A high-stakes question they care about gets them mentally answering before they've decided whether to reply.",
    baseWords: 60,
    minWords: 40,
    maxWords: 100,
    beats: [
      { id: "question", weight: 2, hint: "High-stakes, role-relevant question." },
      { id: "context",  weight: 2, hint: "Brief context for the question." },
      { id: "cta",      weight: 1, hint: "Invite their answer." },
    ],
  },
  {
    id: "directBold",
    label: "Direct & Bold",
    icon: "fa-bullhorn",
    accent: "blue",
    summary: "No fluff. Pitch in two sentences.",
    structureHint:
      "Sentence 1: 'I'll be direct.' or equivalent. Sentence 2: the value prop. Sentence 3: the ask. No small talk.",
    bestFor: "Busy operators, founders, anyone who hates fluff.",
    psychology:
      "Pattern interrupt: most cold emails open with throat-clearing. A direct frame signals respect for the recipient's time and earns the next sentence by being everything other emails aren't.",
    baseWords: 40,
    minWords: 25,
    maxWords: 65,
    beats: [
      { id: "frame", weight: 1, hint: "'I'll be direct.' framing." },
      { id: "value", weight: 2, hint: "The value prop in one breath." },
      { id: "ask",   weight: 1, hint: "The ask. No close." },
    ],
  },
  {
    id: "socialProof",
    label: "Social Proof",
    icon: "fa-trophy",
    accent: "green",
    summary: "Lead with a relevant credibility moment.",
    structureHint:
      "Open with a brief proof (specific result, recognizable name, number). Tie it directly to the recipient. Offer the same outcome.",
    bestFor: "Recipients who need risk reduction before engaging.",
    psychology:
      "Social proof bypasses skepticism — recipients trust 'people like them did this' more than any claim you can make about your product. Specificity (numbers, names) is what makes it credible.",
    baseWords: 80,
    minWords: 60,
    maxWords: 130,
    beats: [
      { id: "proof",   weight: 2, hint: "Specific credibility moment." },
      { id: "connect", weight: 2, hint: "Tie to the recipient." },
      { id: "offer",   weight: 1, hint: "Same outcome for them." },
      { id: "cta",     weight: 1, hint: "Direct ask." },
    ],
  },
  {
    id: "lowFrictionAsk",
    label: "Low-Friction Ask",
    icon: "fa-feather",
    accent: "blue",
    summary: "Tiny ask that makes 'yes' easier than 'no'.",
    structureHint:
      "One sentence of context. One sentence of relevance. CTA = a yes/no question or a 5-minute ask. Nothing more.",
    bestFor: "Top-of-funnel, replies > meetings.",
    psychology:
      "Yes-easier-than-no asks short-circuit the deliberation cost. The lower the perceived effort, the higher the reply rate.",
    baseWords: 35,
    minWords: 20,
    maxWords: 55,
    beats: [
      { id: "context",   weight: 1, hint: "One sentence of context." },
      { id: "relevance", weight: 1, hint: "One sentence of relevance." },
      { id: "ask",       weight: 1, hint: "Yes/no or 5-min ask." },
    ],
  },

  // ========== LONGER, MORE NUANCED FRAMEWORKS (130–250 words) ==========

  {
    id: "permissionOpener",
    label: "Permission Opener",
    icon: "fa-hand",
    accent: "blue",
    summary: "Ask permission first — they grant it, then you pitch.",
    structureHint:
      "Open with a literal permission ask ('Mind if I share something quick?' / 'Quick question — open to a 30-sec read?'). Frame why you reached out (one line of relevance to them). Deliver the value compactly. End with a soft, opt-in CTA that mirrors the permission-frame.",
    bestFor: "Cold outreach to senior decision-makers. People who hate being pitched.",
    psychology:
      "Permission asks invert the normal cold-email power dynamic. By asking first, you signal respect and trigger Cialdini's commitment-and-consistency principle — once they grant micro-permission, they're more open to the actual ask.",
    baseWords: 75,
    minWords: 55,
    maxWords: 110,
    beats: [
      { id: "permission", weight: 1, hint: "The literal permission ask. One sentence." },
      { id: "context",    weight: 1, hint: "Why you reached out — relevance to them." },
      { id: "value",      weight: 2, hint: "The value packed into 1–2 sentences." },
      { id: "cta",        weight: 1, hint: "Opt-in CTA that mirrors the permission frame." },
    ],
  },

  {
    id: "aida",
    label: "AIDA",
    icon: "fa-magnet",
    accent: "amber",
    summary: "Attention · Interest · Desire · Action — proven 4-act arc.",
    structureHint:
      "Attention: a sharp, attention-grabbing opener (stat, claim, observation). Interest: build interest by tying it to their world specifically. Desire: paint the desirable outcome — what life looks like when this is solved. Action: clear, specific CTA.",
    bestFor: "B2B SaaS, mid-funnel outreach where you have a concrete value-prop and time to walk them through it.",
    psychology:
      "AIDA mirrors the natural buying-psychology arc — the recipient must feel attention before they care, care before they want, want before they act. Skipping a beat breaks the chain.",
    baseWords: 130,
    minWords: 100,
    maxWords: 180,
    beats: [
      { id: "attention", weight: 2, hint: "Stat / observation / sharp claim that earns attention." },
      { id: "interest",  weight: 2, hint: "Tie it specifically to their world." },
      { id: "desire",    weight: 2, hint: "Paint the desirable outcome — make them want it." },
      { id: "action",    weight: 1, hint: "Specific, time-bound CTA." },
    ],
  },

  {
    id: "challenger",
    label: "Insight-Led / Challenger",
    icon: "fa-brain",
    accent: "purple",
    summary: "Share an insight that reframes their thinking — then recommend.",
    structureHint:
      "Open with a SPECIFIC insight or data-point about their industry/role they likely don't know. Reframe what it means for them (most people interpret X, but actually Y). Recommend a concrete next step their peers are taking. Close with a CTA tied to learning more.",
    bestFor: "Consultative sales, enterprise outreach, anywhere you sell expertise as much as a product.",
    psychology:
      "Challenger Sale research (CEB) shows top performers in B2B sales win by teaching, tailoring, and taking control of the buying conversation. Insights reposition you from vendor to advisor.",
    baseWords: 130,
    minWords: 100,
    maxWords: 180,
    beats: [
      { id: "insight", weight: 2, hint: "Concrete insight / data-point most don't know." },
      { id: "reframe", weight: 2, hint: "What it actually means for them — counter the common interpretation." },
      { id: "solve",   weight: 2, hint: "What their best peers are doing — concrete recommendation." },
      { id: "cta",     weight: 1, hint: "Soft CTA — 'happy to walk you through' / 'reply if you want the data'." },
    ],
  },

  {
    id: "narrativeTransformation",
    label: "Narrative Transformation",
    icon: "fa-mountain",
    accent: "green",
    summary: "Hero's journey — status quo → catalyst → transformation → invite.",
    structureHint:
      "Open by painting the recipient's current status-quo (where they are now, what's familiar but limiting). Introduce the catalyst (something changed, a new option exists). Paint the transformation — what life looks like after, with concrete proof. Invite them to step into it.",
    bestFor: "Transformation pitches, vision-driven offers, founders selling 'a new way'.",
    psychology:
      "Narrative arc activates the brain's storytelling circuitry — recipients project themselves into the hero role, making the desired outcome feel personally inevitable instead of abstract.",
    baseWords: 160,
    minWords: 120,
    maxWords: 220,
    beats: [
      { id: "before",         weight: 2, hint: "Status quo — where they are now, familiar but limiting." },
      { id: "bridge",         weight: 2, hint: "The catalyst — something changed, a new option exists." },
      { id: "transformation", weight: 3, hint: "Life after — concrete, vivid, proof-backed." },
      { id: "cta",            weight: 1, hint: "Step-into-it invitation." },
    ],
  },

  {
    id: "sixPlusOne",
    label: "6+1 Insight Letter",
    icon: "fa-lightbulb",
    accent: "purple",
    summary: "Bencivenga's framework — context → attention → gap → solve → proof → cta.",
    structureHint:
      "Context: where they are right now (1 sentence — orient them). Attention: a hook that earns the next paragraph (a counter-intuitive line). Gap: the gap between where they are and where they could be (1–2 sentences of pain). Solve: how it's solved (your offer, named clearly). Proof: brief credibility moment (number, name, result). CTA: the ask.",
    bestFor: "Direct-response style B2B outreach. Works especially well when the offer is unfamiliar.",
    psychology:
      "Built by direct-response copywriter Michael Bencivenga, the 6+1 model frontloads orientation so the recipient never feels lost — every paragraph answers the implicit 'so what?' question.",
    baseWords: 170,
    minWords: 130,
    maxWords: 230,
    beats: [
      { id: "context",   weight: 1, hint: "Orient them — 1 sentence about where they are." },
      { id: "attention", weight: 2, hint: "Counter-intuitive hook line that earns the read." },
      { id: "gap",       weight: 2, hint: "The gap between current and possible — 1–2 sentences." },
      { id: "solve",     weight: 2, hint: "Your solve, named clearly." },
      { id: "proof",     weight: 1, hint: "One brief credibility moment." },
      { id: "cta",       weight: 1, hint: "The ask, low-friction." },
    ],
  },

  {
    id: "pastor",
    label: "PASTOR",
    icon: "fa-fire",
    accent: "red",
    summary: "Long-form sales: Problem · Amplify · Story · Transformation · Offer · Response.",
    structureHint:
      "Problem: name the problem in their language. Amplify: agitate the cost of inaction (time, money, opportunity). Story / Solution: a brief client story that mirrors their situation and resolves it. Transformation: the after-state in vivid detail. Offer: what's on the table — clearly. Response: a precise, time-bound ask.",
    bestFor: "Long-form, high-ticket pitches. Audiences ready for depth (warm-ish leads, founder-to-founder, thought-leadership replies).",
    psychology:
      "PASTOR layers emotion (problem + amplify) on logic (story + offer) — covering both the limbic and prefrontal decision systems before the response ask, raising conversion on considered offers.",
    baseWords: 220,
    minWords: 170,
    maxWords: 300,
    beats: [
      { id: "problem",        weight: 1, hint: "Name the problem in their words." },
      { id: "amplify",        weight: 1, hint: "Cost of inaction — time, money, opportunity." },
      { id: "story",          weight: 2, hint: "Brief client story mirroring their situation." },
      { id: "transformation", weight: 2, hint: "After-state in vivid, concrete detail." },
      { id: "offer",          weight: 2, hint: "What's on the table — clearly." },
      { id: "response",       weight: 1, hint: "Precise, time-bound ask." },
    ],
  },

  {
    id: "educationalLetter",
    label: "Educational Letter",
    icon: "fa-graduation-cap",
    accent: "amber",
    summary: "Value-first letter — teach now, sell maybe.",
    structureHint:
      "Open with an insight or observation (no pitch yet). Explain why it matters specifically to them. Give a practical, immediately-usable takeaway (a tactic, framework, or data-point they can act on today). Soft-bridge to your offer ONLY if it's a natural extension. End with a no-pressure CTA.",
    bestFor: "Newsletter-style sequences, top-of-funnel nurture, building authority before pitching.",
    psychology:
      "Reciprocity: by giving genuine value upfront with no immediate ask, you trigger the recipient's instinct to reciprocate — usually with a reply, an open mind, or eventually a meeting. Works best when the takeaway is genuinely useful in isolation.",
    baseWords: 180,
    minWords: 140,
    maxWords: 240,
    beats: [
      { id: "insight",   weight: 2, hint: "An insight or observation — no pitch." },
      { id: "relevance", weight: 1, hint: "Why it matters specifically to them." },
      { id: "value",     weight: 3, hint: "Practical, immediately-usable takeaway." },
      { id: "bridge",    weight: 1, hint: "Soft-bridge to your offer ONLY if natural." },
      { id: "cta",       weight: 1, hint: "No-pressure CTA — opt-in only." },
    ],
  },
];

// Map for O(1) lookup when rendering existing variant data.
export const STRUCTURE_BY_ID = STRUCTURE_LIBRARY.reduce((map, s) => {
  map[s.id] = s;
  return map;
}, {});

// =============================================================================
// LENGTH TWEAKS — relative knobs that scale the structure's natural length.
//
// The first four tweaks (shorter / natural / longer / clone) stay inside the
// structure's psychological floor/ceiling — they only NUDGE the natural
// length. The next two (extended / longForm) and the custom option are
// opt-in "stretch" tweaks: they intentionally allow the target to grow
// beyond `structure.maxWords` (the structure's natural ceiling), capped only
// by a hard safety ceiling (`LENGTH_HARD_CEILING`). They exist because some
// campaigns genuinely need more room than the structure's natural shape —
// the user explicitly accepts that the structure is being stretched.
//
// `multiplier`:
//   - number → multiplied against `structure.baseWords`. By default clamped
//     to [structure.minWords, structure.maxWords]; tweaks with
//     `bypassMax: true` may exceed the structure's natural ceiling and are
//     only clamped at the safety ceiling.
//   - "clone"  → use the clone's own avg word count (from voiceDNA.rhythm).
//     Falls back to baseWords if the clone hasn't been trained yet.
//   - "custom" → use the variant's stored `customWords` value (free-form
//     target word count). Clamped to [structure.minWords, hard ceiling].
// =============================================================================

// Safety bounds for stretch tweaks and custom word counts. Picked so that
// `longForm` (~2.2× baseWords) on the longest structure (PASTOR, baseWords
// 220 → ~485) still fits comfortably, while clearly bounded so a stray
// click can't generate a 5000-word email and burn tokens.
export const LENGTH_HARD_CEILING = 800;
export const LENGTH_HARD_FLOOR = 20;

// Default seed value when the user first switches a variant to "Custom".
// Chosen to feel like a noticeable step up from the structure's natural
// ceiling without being absurd — the user is expected to tune it from here.
export const CUSTOM_WORDS_DEFAULT = 250;

export const LENGTH_TWEAKS = [
  {
    id: "shorter",
    label: "Shorter",
    icon: "fa-down-left-and-up-right-to-center",
    description: "Tighter than the structure's natural length.",
    multiplier: 0.7,
  },
  {
    id: "natural",
    label: "Natural",
    icon: "fa-equals",
    description: "Use the structure's psychologically-correct base length.",
    multiplier: 1.0,
  },
  {
    id: "longer",
    label: "Longer",
    icon: "fa-up-right-and-down-left-from-center",
    description: "More breathing room. Still inside the structure's safe range.",
    multiplier: 1.35,
  },
  {
    id: "extended",
    label: "Extended",
    icon: "fa-up-long",
    description:
      "Pushes past the structure's natural ceiling for more depth. Best when proof or context need real space.",
    multiplier: 1.7,
    bypassMax: true,
  },
  {
    id: "longForm",
    label: "Long-form",
    icon: "fa-align-justify",
    description:
      "Long-form treatment. Substantially more room — use for high-intent or thought-leadership outreach.",
    multiplier: 2.2,
    bypassMax: true,
  },
  {
    id: "custom",
    label: "Custom",
    icon: "fa-sliders",
    description:
      "Set the target word count yourself (e.g. ~300 words). Use when none of the presets fit.",
    multiplier: "custom",
    bypassMax: true,
  },
  {
    id: "clone",
    label: "Mirror clone",
    icon: "fa-fingerprint",
    description: "Match how this clone naturally writes (from training data).",
    multiplier: "clone",
  },
];

// Clamp helper for the user-typed custom word count. Stays safe even on
// pasted / garbage input and never returns a value outside the hard bounds.
export const sanitizeCustomWords = (n) => {
  const parsed = Math.round(Number(n));
  if (!Number.isFinite(parsed) || parsed <= 0) return CUSTOM_WORDS_DEFAULT;
  if (parsed < LENGTH_HARD_FLOOR) return LENGTH_HARD_FLOOR;
  if (parsed > LENGTH_HARD_CEILING) return LENGTH_HARD_CEILING;
  return parsed;
};

export const LENGTH_TWEAK_BY_ID = LENGTH_TWEAKS.reduce((m, t) => {
  m[t.id] = t;
  return m;
}, {});

// =============================================================================
// VOICE & BRIEF ADHERENCE — two independent dials that replace Standard mode's
// single Adherence slider. VC has TWO things to obey: the writer's voice and
// the campaign brief. The user wants different strictness on each.
//
//   VOICE  → how strictly the model mirrors the clone's style patterns
//            (rhythm, signature words, opening style, quirks).
//   BRIEF  → how strictly the model surfaces every campaign brief item
//            (references, what makes us special, pain points, goal, angles, fixed facts).
//
// The id is what gets persisted and read by the backend prompt builder
// (voiceCloneEmailPrompt.js → renderVoiceAdherence / renderBriefAdherence).
// =============================================================================

export const VOICE_ADHERENCE_LEVELS = [
  {
    id: "free",
    label: "Free",
    icon: "fa-feather",
    description:
      "Use the voice as a soft inspiration. The model is freer to write what fits the recipient best.",
  },
  {
    id: "balanced",
    label: "Balanced",
    icon: "fa-balance-scale",
    description:
      "Voice patterns guide the rhythm and word choice but don't constrain every sentence.",
  },
  {
    id: "strict",
    label: "Strict",
    icon: "fa-crosshairs",
    description:
      "Most sentences must reflect at least one DNA pattern (rhythm, signature word, opening style, quirk).",
  },
  {
    id: "mirror",
    label: "Mirror clone",
    icon: "fa-fingerprint",
    description:
      "Maximum voice fidelity — the email reads as if the clone wrote it. Generic phrasing must be rewritten.",
  },
];

export const VOICE_ADHERENCE_BY_ID = VOICE_ADHERENCE_LEVELS.reduce(
  (m, v) => { m[v.id] = v; return m; },
  {},
);

// Faithful-by-default: a fresh clone template should copy the writer's
// trained voice as closely as possible (the user "feeds the brain and the
// brain writes the next email exactly in their style"). Topic/industry drift
// is opt-in via STYLE_ADAPTATION_DEFAULT below.
export const VOICE_ADHERENCE_DEFAULT = "mirror";

// =============================================================================
// STYLE / TOPIC ADAPTATION — opt-in escape hatch from faithful cloning.
//
// OFF (default): the clone reproduces the writer's trained subject + body
// style as-is, swapping only recipient-specific content — even if the
// recipient's industry differs a little from the template's examples.
// ON: the writer has decided the campaign topic/industry has changed enough
// that the AI may adapt and drift from the trained style. The editor shows a
// warning when this is enabled because the output may no longer sound exactly
// like the writer's calibrated voice.
// =============================================================================
export const STYLE_ADAPTATION_DEFAULT = false;

// =============================================================================
// STRUCTURE SOURCE — where the email's STRUCTURE comes from.
//
//   'twin'   → copy the structure from the CopyTwin. The email reproduces the
//              writer's OWN architecture (opening/argument/closing rhythm,
//              paragraphing) and length, derived from their Voice DNA. No fixed
//              library structure is imposed; the static structure variants are
//              optional (only used if the user switches to 'custom').
//   'custom' → use the user-configured structure variants (the curated
//              STRUCTURE_LIBRARY beats + A/B testing). The historic behavior.
//
// Default for NEW templates is 'twin' (the clone writes in its own structure
// out of the box). A MISSING value is treated as 'custom' by the backend so
// existing templates that rely on configured variants keep their exact
// behavior until the user opts them in.
// =============================================================================
export const STRUCTURE_SOURCES = { TWIN: "twin", CUSTOM: "custom" };
export const STRUCTURE_SOURCE_DEFAULT = STRUCTURE_SOURCES.TWIN;

// Resolve the effective source for an existing template. Missing → 'custom'
// (preserve behavior); only an explicit 'twin' enables twin-mirror.
export const resolveStructureSource = (cloneConfig) =>
  cloneConfig?.structureSource === STRUCTURE_SOURCES.TWIN
    ? STRUCTURE_SOURCES.TWIN
    : STRUCTURE_SOURCES.CUSTOM;

export const BRIEF_ADHERENCE_LEVELS = [
  {
    id: "loose",
    label: "Loose",
    icon: "fa-feather-pointed",
    description:
      "Pull from the brief only when it strengthens the email. The model may skip points.",
  },
  {
    id: "balanced",
    label: "Balanced",
    icon: "fa-scale-balanced",
    description:
      "Cover the most relevant brief points naturally. Not every item has to be mentioned.",
  },
  {
    id: "strict",
    label: "Strict",
    icon: "fa-list-check",
    description:
      "Every brief item (references, special, goal, angles) must be reflected in the email.",
  },
];

export const BRIEF_ADHERENCE_BY_ID = BRIEF_ADHERENCE_LEVELS.reduce(
  (m, v) => { m[v.id] = v; return m; },
  {},
);

export const BRIEF_ADHERENCE_DEFAULT = "balanced";

export const resolveVoiceAdherence = (id) =>
  VOICE_ADHERENCE_BY_ID[id] || VOICE_ADHERENCE_BY_ID[VOICE_ADHERENCE_DEFAULT];

export const resolveBriefAdherence = (id) =>
  BRIEF_ADHERENCE_BY_ID[id] || BRIEF_ADHERENCE_BY_ID[BRIEF_ADHERENCE_DEFAULT];

// =============================================================================
// CREATIVITY — how much the model varies wording / structure / angle between
// emails. This is the model's sampling temperature, surfaced as a plain-English
// dial. It is INDEPENDENT of voice adherence: voice adherence controls HOW
// closely the output copies the writer's trained style; creativity controls
// HOW MUCH the output varies from one email (or regeneration) to the next.
//
// Low creativity → the clone keeps picking the same proven phrasing/closer
// (the "every email is 90% identical" symptom). Higher creativity → noticeably
// fresher wording and angles per send, while still respecting voice + brief.
//
// The `temperature` here is the source of truth consumed by the backend
// (generateVoiceCloneEmail mirrors this mapping). Anthropic clamps to [0,1].
// =============================================================================
export const CREATIVITY_LEVELS = [
  {
    id: "precise",
    label: "Precise",
    icon: "fa-bullseye",
    temperature: 0.6,
    description:
      "Very consistent. Sticks closely to the proven wording — least variety between emails.",
  },
  {
    id: "balanced",
    label: "Balanced",
    icon: "fa-scale-balanced",
    temperature: 0.85,
    description:
      "A healthy mix of consistency and fresh wording. Good default for most campaigns.",
  },
  {
    id: "high",
    label: "Creative",
    icon: "fa-lightbulb",
    temperature: 0.95,
    description:
      "More variety in wording, openings and angles from one email to the next.",
  },
  {
    id: "max",
    label: "Max",
    icon: "fa-wand-magic-sparkles",
    temperature: 1.0,
    description:
      "Maximum variety. Each email takes a noticeably different take — least repetition.",
  },
];

export const CREATIVITY_BY_ID = CREATIVITY_LEVELS.reduce(
  (m, c) => { m[c.id] = c; return m; },
  {},
);

// Default keeps the historic behavior (~0.85) so existing templates don't
// shift; the user dials up to "Creative" / "Max" when they want more variety.
export const CREATIVITY_DEFAULT = "balanced";

export const resolveCreativity = (id) =>
  CREATIVITY_BY_ID[id] || CREATIVITY_BY_ID[CREATIVITY_DEFAULT];

// =============================================================================
// SIGN-OFF IDENTITY — per-template override of who the email is signed as.
//
// Use case: workspace has multiple sender accounts (different domains,
// different team members) but a campaign template should always sign as the
// same person — or workspace `user_name` is empty and we don't want the
// model to invent "Sender" as a fallback. When mode === 'custom', the
// backend hands the configured name/role into the prompt as the effective
// sender identity, overriding the workspace's default.
// =============================================================================
export const SIGN_OFF_IDENTITY_MODES = {
  SENDER: 'sender',  // default — use the workspace sender's name/email/role
  CUSTOM: 'custom',  // use the explicit values configured below
};

export const SIGN_OFF_IDENTITY_DEFAULT = {
  mode: SIGN_OFF_IDENTITY_MODES.SENDER,
  // Single free-text "name" field. The user types EXACTLY what should
  // appear in the closing — first only ("Jan"), full name with space
  // ("Jan Schubert"), or even a title ("Dr. Jan Schubert"). The backend
  // pipes this verbatim into the sign-off — no first/last parsing — so
  // whatever the user writes is what the recipient sees.
  name: '',
  role: '',
};

export const resolveSignOffIdentity = (signOffIdentity) => {
  const raw = signOffIdentity || SIGN_OFF_IDENTITY_DEFAULT;
  const mode =
    raw.mode === SIGN_OFF_IDENTITY_MODES.CUSTOM
      ? SIGN_OFF_IDENTITY_MODES.CUSTOM
      : SIGN_OFF_IDENTITY_MODES.SENDER;
  // Only trim the edges so internal spaces (e.g. "Jan Schubert") are
  // preserved. Collapsing 2+ runs guards against double-spaces from
  // accidental pastes but never strips the single space the user typed.
  const name = String(raw.name || '').trim().replace(/[ \t]{2,}/g, ' ');
  return {
    mode,
    name,
    role: String(raw.role || '').trim(),
  };
};

// =============================================================================
// GREETING RULES — per-template control over the email's opener.
//
// Two concrete cases the user told us about:
//   1. We know a decision maker (have a first/last name): write something
//      addressed to that person ("Hey Dieter," / "Sehr geehrter Herr Rotlauer,").
//   2. We only have a generic / info@ mail with no DM: address the company
//      or the team ("Liebes Acme-Team," / "Hallo Acme-Team,").
//
// Stored as two free-text templates with placeholder tokens the backend
// resolves at generation time. When `mode === 'auto'` the backend keeps
// the legacy behavior (let voice DNA dictate the opener) — same as
// templates that existed before this field was introduced.
//
// Placeholders supported in the templates:
//   {{firstName}}    — DM first name           (e.g. "Dieter")
//   {{lastName}}     — DM last name            (e.g. "Rotlauer")
//   {{recipientName}} — DM full name           (e.g. "Dieter Rotlauer")
//   {{businessName}} — recipient company       (e.g. "Acme GmbH")
// =============================================================================
export const GREETING_MODES = {
  AUTO:       'auto',        // let the voice DNA decide — pre-feature behavior
  CONFIGURED: 'configured',  // use the templates below verbatim
};

export const GREETING_PLACEHOLDER_TOKENS = [
  'firstName',
  'lastName',
  'recipientName',
  'businessName',
  'businessNameShort',
];

// Reasonable starter templates — chosen to bias the user toward an
// informal-yet-respectful opener, which performs best in DACH B2B cold
// outreach. Easily overridden in the editor.
export const GREETING_DEFAULT_WITH_FIRST_NAME =
  'Hi {{firstName}},';
export const GREETING_DEFAULT_WITHOUT_FIRST_NAME =
  'Hallo {{businessName}}-Team,';

// Company mention rule — overrides whatever the voice DNA would naturally
// do with the company name in the opening line. AUTO keeps the legacy
// behavior (DNA decides), INCLUDE forces the AI to weave the company
// (preferably the short form) into the greeting, EXCLUDE forbids it.
export const GREETING_COMPANY_MENTION = {
  AUTO:    'auto',
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
};

// 4-step context-depth slider. Each level translates into a concrete prompt
// directive (see voiceCloneEmailPrompt → renderGreetingPersonalization).
// Order matters: index 0 = least personalized, index 3 = deepest tie-in.
export const GREETING_COMPANY_CONTEXT_LEVELS = [
  {
    id: 'minimal',
    label: 'Minimal',
    description: "Plain greeting, no company-specific framing.",
  },
  {
    id: 'light',
    label: 'Light',
    description: "Mention the company briefly if it fits naturally.",
  },
  {
    id: 'grounded',
    label: 'Grounded',
    description: "Ground the opener in the recipient's company context.",
  },
  {
    id: 'deep',
    label: 'Deep',
    description: "Lean into specific signals (industry, product, role) right in the opener.",
  },
];

export const GREETING_COMPANY_CONTEXT_BY_ID =
  GREETING_COMPANY_CONTEXT_LEVELS.reduce((m, l) => { m[l.id] = l; return m; }, {});

export const GREETING_COMPANY_CONTEXT_DEFAULT = 'grounded';

export const resolveGreetingCompanyContext = (id) =>
  GREETING_COMPANY_CONTEXT_BY_ID[id]
  || GREETING_COMPANY_CONTEXT_BY_ID[GREETING_COMPANY_CONTEXT_DEFAULT];

export const GREETING_DEFAULT = {
  mode: GREETING_MODES.AUTO,
  withFirstName:    GREETING_DEFAULT_WITH_FIRST_NAME,
  withoutFirstName: GREETING_DEFAULT_WITHOUT_FIRST_NAME,
  // Personalization controls — apply to BOTH modes. In CONFIGURED mode the
  // literal template still wins for the opening line, but the context level
  // and mention rule still steer how much the rest of the email surrounding
  // the greeting leans on the company. In AUTO mode they fully control
  // whether/how the DNA references the company in the opener.
  companyMention: GREETING_COMPANY_MENTION.AUTO,
  companyContextLevel: GREETING_COMPANY_CONTEXT_DEFAULT,
};

const resolveCompanyMention = (raw) => {
  if (raw === GREETING_COMPANY_MENTION.INCLUDE) return GREETING_COMPANY_MENTION.INCLUDE;
  if (raw === GREETING_COMPANY_MENTION.EXCLUDE) return GREETING_COMPANY_MENTION.EXCLUDE;
  return GREETING_COMPANY_MENTION.AUTO;
};

export const resolveGreeting = (greeting) => {
  const raw = greeting || GREETING_DEFAULT;
  const mode =
    raw.mode === GREETING_MODES.CONFIGURED
      ? GREETING_MODES.CONFIGURED
      : GREETING_MODES.AUTO;
  return {
    mode,
    withFirstName:    String(raw.withFirstName    || GREETING_DEFAULT_WITH_FIRST_NAME),
    withoutFirstName: String(raw.withoutFirstName || GREETING_DEFAULT_WITHOUT_FIRST_NAME),
    companyMention:        resolveCompanyMention(raw.companyMention),
    companyContextLevel:   resolveGreetingCompanyContext(raw.companyContextLevel).id,
  };
};

// =============================================================================
// SMART BUSINESS-NAME SHORTENING
//
// Strips legal-form suffixes (GmbH, AG, Inc., Ltd., LLC, & Co. KG, …) and
// trailing junk so the resulting string is the BRANDABLE core users would
// actually say out loud. Empty / single-word inputs round-trip unchanged so
// it's safe to plug into placeholder resolution anywhere the full business
// name is too clunky (e.g. greetings: "Hallo Müller Optik GmbH-Team," →
// "Hallo Müller Optik-Team,").
//
// Heuristic, NOT an LLM call — deterministic, fast, predictable, free per
// send. The prompt builder ALSO instructs the model to prefer the short
// form when generating greetings, which catches the long-tail (e.g. very
// idiosyncratic company names) the heuristic can't reach.
// =============================================================================
const LEGAL_SUFFIX_PATTERNS = [
  /\bGmbH(?:\s*&\s*Co\.?\s*K[GA]\.?)?\b/gi,
  /\bUG(?:\s*\(\s*haftungsbeschr[äa]nkt\s*\))?\b/gi,
  /\bAG\b/g,
  /\bKG[aA]?\b/g,
  /\bOHG\b/g,
  /\bGbR\b/g,
  /\be\.?\s*K\.?(?:fr|fm)?\b/gi,
  /\bLtd\.?\b/gi,
  /\bL\.?L\.?C\.?\b/gi,
  /\bL\.?L\.?P\.?\b/gi,
  /\bInc\.?\b/gi,
  /\bCorp(?:oration)?\.?\b/gi,
  /\bCompany\b/gi,
  /\bCo\.?\b/gi,
  /\bN\.?V\.?\b/gi,
  /\bB\.?V\.?\b/gi,
  /\bS\.?A\.?(?:R\.?L\.?|S\.?)?\b/gi,
  /\bS\.?R\.?L\.?\b/gi,
  /\bS\.?p\.?A\.?\b/gi,
  /\bA\.?S\.?\b/gi,
  /\bPlc\.?\b/gi,
  /\bPty\.?\s*Ltd\.?\b/gi,
  /\bPty\.?\b/gi,
];

export const buildShortBusinessName = (businessName) => {
  const raw = String(businessName || '').trim();
  if (!raw) return '';
  let core = raw;
  for (const pat of LEGAL_SUFFIX_PATTERNS) core = core.replace(pat, ' ');
  core = core
    .replace(/[&,\u2013\u2014]/g, ' ')
    .replace(/[-\s]+$/g, '')
    .replace(/^[-\s]+/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!core) return raw;
  const words = core.split(' ').filter(Boolean);
  if (words.length === 0) return raw;
  if (words.length <= 3) return words.join(' ');
  return words.slice(0, 3).join(' ');
};

// Pure helper — substitutes the supported tokens in a greeting template
// against a contact-shaped object. Empty tokens are stripped so the user
// never sees something like "Hallo -Team,". Used by the backend prompt
// builder; kept in this shared file so the editor preview can mirror the
// same logic when we add a "live greeting preview" later.
export const applyGreetingPlaceholders = (template, ctx = {}) => {
  if (!template) return '';
  const firstName     = String(ctx.firstName     || '').trim();
  const lastName      = String(ctx.lastName      || '').trim();
  const recipientName = String(ctx.recipientName || '').trim();
  const businessName  = String(ctx.businessName  || '').trim();
  const businessNameShort = buildShortBusinessName(businessName);

  const filled = String(template)
    .replace(/\{\{\s*firstName\s*\}\}/gi,         firstName)
    .replace(/\{\{\s*lastName\s*\}\}/gi,          lastName)
    .replace(/\{\{\s*recipientName\s*\}\}/gi,     recipientName || firstName || businessName)
    .replace(/\{\{\s*businessNameShort\s*\}\}/gi, businessNameShort)
    .replace(/\{\{\s*businessName\s*\}\}/gi,      businessName);

  return filled
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/[-\u2013\u2014]\s*([,.!?;:])/g, '$1')
    .replace(/\s+-(?=\s|$)/g, '')
    .trim();
};

// Backwards-compat: map old absolute-length IDs (from before this refactor)
// to the closest tweak so existing variants still render meaningfully.
const LEGACY_LENGTH_TO_TWEAK = {
  ultraShort: "shorter",
  short: "shorter",
  medium: "natural",
  long: "longer",
};

export const resolveLengthTweakId = (id) => {
  if (!id) return "natural";
  if (LENGTH_TWEAK_BY_ID[id]) return id;
  return LEGACY_LENGTH_TO_TWEAK[id] || "natural";
};

// =============================================================================
// BEAT BREAKDOWN — given a structure + length tweak (+ optional clone), return
// the per-beat split with concrete word counts. The single source of truth
// for both the picker preview and the variant card blueprint, AND the
// generation prompt later (Step 5/6 backend).
// =============================================================================

export const computeTargetWords = (
  structure,
  tweakId,
  cloneAvgWords,
  customWords,
) => {
  if (!structure) return 0;
  const tweak = LENGTH_TWEAK_BY_ID[tweakId] || LENGTH_TWEAK_BY_ID.natural;
  let target;
  if (tweak.multiplier === "custom") {
    target = sanitizeCustomWords(customWords);
  } else if (tweak.multiplier === "clone") {
    target = cloneAvgWords && cloneAvgWords > 0 ? cloneAvgWords : structure.baseWords;
  } else {
    target = structure.baseWords * tweak.multiplier;
  }
  // Default clamp = the structure's psychological floor/ceiling (so we don't
  // break its natural shape). Stretch tweaks (`bypassMax`) intentionally
  // allow the target to grow beyond the structure's ceiling — the user has
  // opted into a longer form on purpose. The hard ceiling is still respected
  // as a safety net.
  const floor = structure.minWords;
  const ceiling = tweak.bypassMax ? LENGTH_HARD_CEILING : structure.maxWords;
  target = Math.max(floor, Math.min(ceiling, target));
  return Math.round(target);
};

export const computeBeatBreakdown = (
  structureId,
  tweakId,
  cloneAvgWords,
  customWords,
) => {
  const structure = STRUCTURE_BY_ID[structureId];
  if (!structure || !structure.beats?.length) return [];
  const targetWords = computeTargetWords(
    structure,
    tweakId,
    cloneAvgWords,
    customWords,
  );
  const totalWeight = structure.beats.reduce((s, b) => s + b.weight, 0) || 1;
  return structure.beats.map((b) => {
    const ratio = b.weight / totalWeight;
    return {
      ...b,
      meta: BEAT_META[b.id] || { label: b.id, color: "#94a3b8" },
      ratio,
      pct: Math.round(ratio * 100),
      words: Math.round(ratio * targetWords),
    };
  });
};

// =============================================================================
// CAMPAIGN LANGUAGES — what language the generated emails are written in.
//
// Stored as the same human-readable English name the existing Standard-mode
// pipeline already uses ("English", "German", "French"…) so the backend
// (`resolveInstructions`, `resolveSmartSubject` etc.) can consume it without
// any translation layer when Step 6 wires up Voice-Clone sends.
//
// The editor UI itself only translates between English and German — every
// other campaign language still shows the editor in English (since most users
// are EN/DE native and the rest of the app is English-only).
// =============================================================================

export const CAMPAIGN_LANGUAGES = [
  { value: "English",    code: "EN", flag: "🇬🇧", uiLang: "en" },
  { value: "German",     code: "DE", flag: "🇩🇪", uiLang: "de" },
  { value: "French",     code: "FR", flag: "🇫🇷", uiLang: "en" },
  { value: "Spanish",    code: "ES", flag: "🇪🇸", uiLang: "en" },
  { value: "Italian",    code: "IT", flag: "🇮🇹", uiLang: "en" },
  { value: "Portuguese", code: "PT", flag: "🇵🇹", uiLang: "en" },
  { value: "Dutch",      code: "NL", flag: "🇳🇱", uiLang: "en" },
  { value: "Polish",     code: "PL", flag: "🇵🇱", uiLang: "en" },
  { value: "Turkish",    code: "TR", flag: "🇹🇷", uiLang: "en" },
  { value: "Swedish",    code: "SV", flag: "🇸🇪", uiLang: "en" },
  { value: "Japanese",   code: "JA", flag: "🇯🇵", uiLang: "en" },
  { value: "Chinese",    code: "ZH", flag: "🇨🇳", uiLang: "en" },
];

export const CAMPAIGN_LANGUAGE_BY_VALUE = CAMPAIGN_LANGUAGES.reduce(
  (m, l) => { m[l.value] = l; return m; },
  {},
);

export const CAMPAIGN_LANGUAGE_DEFAULT = "English";

// Resolve which UI translation pack to use for the editor. We only ship
// English + German strings; any other campaign language falls back to English
// (so the user still gets a usable editor regardless of campaign language).
export const resolveEditorUILanguage = (campaignLanguage) => {
  const lang = CAMPAIGN_LANGUAGE_BY_VALUE[campaignLanguage];
  return lang?.uiLang === "de" ? "de" : "en";
};

// =============================================================================
// CONTACT FIELDS — what the AI gets to know about each recipient.
//
// Standard fields are a curated subset of `BASE_AVAILABLE_FIELDS` from
// NewWorldDbTable.js — the ones that meaningfully drive personalization.
// Auto-selected for Voice-Clone-mode templates; user can deselect any of them
// per-campaign.
//
// Custom fields are admin-defined under `World/{userId}/settings/customFields`
// and shipped via the `useCustomFields` hook. Default selection in clone mode
// = those marked `useAsEmailContext: true` in the admin (sane default), but
// the user can override per-campaign.
// =============================================================================

export const STANDARD_PERSONALIZATION_FIELDS = [
  { key: "business_name",            label: "Business Name",       icon: "fa-building",      essential: true },
  { key: "lead_website",             label: "Website",             icon: "fa-globe",         essential: true },
  { key: "lead_description",         label: "Business Description",icon: "fa-align-left",    essential: true },
  { key: "lead_city",                label: "City",                icon: "fa-location-dot",  essential: false },
  { key: "lead_country",             label: "Country",             icon: "fa-flag",          essential: false },
  { key: "lead_email",               label: "Recipient Email",     icon: "fa-envelope",      essential: false },
  { key: "lead_telephone",           label: "Phone",               icon: "fa-phone",         essential: false },
  { key: "lead_decision_makers",     label: "Decision Makers",     icon: "fa-user-tie",      essential: false },
  { key: "lead_socials",             label: "Social Links",        icon: "fa-share-nodes",   essential: false },
  { key: "lead_instagram_followers", label: "IG Followers",        icon: "fa-user-group",    essential: false },
  { key: "lead_rating",              label: "Rating",              icon: "fa-star",          essential: false },
  { key: "lead_reviews",             label: "Reviews",             icon: "fa-comments",      essential: false },
];

export const STANDARD_FIELD_BY_KEY = STANDARD_PERSONALIZATION_FIELDS.reduce(
  (m, f) => { m[f.key] = f; return m; },
  {},
);

// Default-on subset on first creation: essentials + a sensible baseline.
export const DEFAULT_ENABLED_STANDARD_KEYS = STANDARD_PERSONALIZATION_FIELDS
  .filter((f) => f.essential || ["lead_city", "lead_decision_makers"].includes(f.key))
  .map((f) => f.key);

// =============================================================================
// OPTIONAL ENHANCEMENTS — additive knobs beyond raw field data.
//
// NOTE on web research: BonusClub's research tools (Instagram-Insight,
// Website-Analysis, SEO, Outreach-Notes) populate the contact row with rich
// research data BEFORE generation runs. The Voice-Clone prompt picks that
// pre-fetched data up automatically through `contact.research` — no per-
// template toggle needed. We do NOT spawn live web fetches at send time
// (latency + cost + duplication of what the research tools already produce).
// The previous "webResearch" toggle was misleading and has been removed.
// =============================================================================

export const INFO_ENHANCEMENTS = [
  {
    id: "recipientHistory",
    labelKey: "enhRecipientHistoryLabel",
    descKey: "enhRecipientHistoryDesc",
    label: "Prior interaction history",
    description:
      "If we've contacted them before, reference past touchpoints respectfully.",
    icon: "fa-clock-rotate-left",
    defaultOn: false,
  },
  {
    id: "senderContext",
    labelKey: "enhSenderContextLabel",
    descKey: "enhSenderContextDesc",
    label: "Sender context",
    description:
      "Inject the sender's role / business context for relevance framing.",
    icon: "fa-user-tie",
    defaultOn: true,
  },
];

// =============================================================================
// INFO ADD-ONS — flexible pools of campaign-specific material the user can
// attach to a template. The presets cover the most common cold-email content
// categories; "custom" lets the user define their own bucket.
//
// At generation time the backend surfaces every addon to the AI as scoped,
// labeled material it may draw from (different from the strict-whitelist
// `fixedFacts` — addons are richer / longer items the AI can pick from
// contextually).
// =============================================================================

export const INFO_ADDON_PRESETS = [
  { id: "case_studies",  icon: "fa-trophy",         label: "Case studies"     },
  { id: "testimonials",  icon: "fa-quote-right",    label: "Testimonials"     },
  { id: "notable_clients", icon: "fa-building",     label: "Notable clients"  },
  { id: "stats",         icon: "fa-chart-column",   label: "Stats & metrics"  },
  { id: "press",         icon: "fa-newspaper",      label: "Press mentions"   },
  { id: "credentials",   icon: "fa-medal",          label: "Credentials"      },
  { id: "background",    icon: "fa-circle-info",    label: "Background"       },
  { id: "objections",    icon: "fa-circle-question",label: "Objections + answers" },
  { id: "trust",         icon: "fa-shield-halved",  label: "Trust signals"    },
  { id: "recent_wins",   icon: "fa-bolt",           label: "Recent wins"      },
  { id: "custom",        icon: "fa-pen-to-square",  label: "Custom"           },
];

export const INFO_ADDON_PRESET_BY_ID = INFO_ADDON_PRESETS.reduce(
  (m, p) => { m[p.id] = p; return m; },
  {},
);

export const generateInfoAddonId = () =>
  `ia_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export const createInfoAddon = (presetId, customLabel) => {
  const preset = INFO_ADDON_PRESET_BY_ID[presetId] || INFO_ADDON_PRESET_BY_ID.custom;
  return {
    id: generateInfoAddonId(),
    type: presetId === "custom" ? "custom" : preset.id,
    label: customLabel || preset.label,
    icon: preset.icon,
    items: [],
  };
};

// =============================================================================
// QUALITY TIERS — neutral user-facing scale that maps internally to the actual
// generation engines. We deliberately do NOT expose the underlying model
// names ("Claude Haiku / Sonnet / Opus") in the UI — to the user it's all
// "BonusClub AI" with a quality knob.
//
// Higher tiers = better model + more max tokens + (where useful) extended
// thinking budget. The mapping lives here so frontend + backend stay in sync,
// but only the backend (Cloud Functions) ever speaks the model strings.
// =============================================================================

// Three tiers, no Opus. Haiku is "Standard" (cost-efficient, fast, great with
// rich DNA + brief context), Sonnet is "Best" (premium quality, sharper voice
// match), and "Ultra" is Sonnet WITH bounded extended thinking — the model
// reasons about recipient fit and brief weaving before writing, for hero
// sends where it's worth the extra cost. The user-facing name hides the
// model layer; internally the backend reads `engine` + `thinking`.
export const QUALITY_TIER_DEFAULT = "best";

// Credit pricing per generation. Aligned with the existing standard pipeline
// where a Standard template send = 0.5 credits.
//   Standard (Haiku)        → 0.25 (cheaper than Standard — Haiku is ~1/5 the cost)
//   Best (Sonnet)           → 0.5  (matches a normal Standard send — fair price for premium voice)
//   Ultra (Sonnet+thinking) → 1.0  (thinking adds output-priced reasoning tokens)
export const QUALITY_TIERS = [
  {
    id: "good",
    label: "Standard",
    icon: "fa-wand-sparkles",
    tagline: "Fast and cost-efficient. Great for high-volume bulk sends.",
    speed: 4,    // 4 = fastest
    quality: 2,  // solid quality with a well-trained DNA + brief
    creditCost: 0.25,
    // Backend resolution (used by the Cloud Function):
    engine: "haiku",
    maxTokens: 1500,
    thinking: false,
  },
  {
    id: "best",
    label: "Best",
    icon: "fa-crown",
    tagline: "Premium quality. Sharper voice match for hero campaigns.",
    speed: 3,
    quality: 3,
    creditCost: 0.5,
    engine: "sonnet",
    maxTokens: 2500,
    thinking: false,
  },
  {
    id: "ultra",
    label: "Ultra",
    icon: "fa-brain",
    tagline:
      "Maximum thinking. The AI reasons about the recipient and weaves in research before writing. For your most important sends.",
    speed: 2,    // slower — extended thinking takes time
    quality: 4,
    creditCost: 1.0,
    engine: "sonnet",
    maxTokens: 4000,   // raised to leave room for prose after the thinking budget
    thinking: true,
  },
];

// Action key written to the credit log when this tier is consumed. Mirrors
// the convention used by Bulk / Outreach (`email_generation`, `hyperintelligence_email`).
export const CREDIT_ACTION_PREVIEW = "voice_clone_preview";
export const CREDIT_ACTION_SEND = "voice_clone_send";

// Format a credit cost for display. Strips trailing zeros so 0.5 stays "0.5"
// and 5 shows as "5".
export const formatCreditCost = (n) => {
  if (n == null || isNaN(n)) return "0";
  return Number(n).toString();
};

export const QUALITY_TIER_BY_ID = QUALITY_TIERS.reduce((m, t) => {
  m[t.id] = t;
  return m;
}, {});

// Legacy 4-tier IDs (basic / fine) silently resolve to the closest of the
// two new tiers so templates created before this simplification keep
// working without manual migration.
const LEGACY_QUALITY_TIER_MAP = {
  basic: "good",  // basic was the cheapest → maps to Standard (Haiku)
  fine:  "best",  // fine was a premium step → maps to Best (Sonnet)
};

export const resolveQualityTier = (id) => {
  const mapped = LEGACY_QUALITY_TIER_MAP[id] || id;
  return QUALITY_TIER_BY_ID[mapped] || QUALITY_TIER_BY_ID[QUALITY_TIER_DEFAULT];
};

// =============================================================================
// VARIANT FACTORIES + HELPERS
// =============================================================================

export const generateVariantId = () =>
  `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export const createVariant = (structureId, lengthTweakId, customWords) => {
  const structure = STRUCTURE_BY_ID[structureId];
  const tweakId = resolveLengthTweakId(lengthTweakId);
  const tweak = LENGTH_TWEAK_BY_ID[tweakId];
  if (!structure || !tweak) return null;
  const isCustom = tweak.multiplier === "custom";
  return {
    id: generateVariantId(),
    structureId,
    lengthTweakId: tweakId,
    // Only persist a customWords value when the chosen tweak actually uses
    // it — keeps the variant shape clean for the existing 4 tweak ids and
    // avoids polluting old data with a field that has no effect.
    ...(isCustom ? { customWords: sanitizeCustomWords(customWords) } : {}),
    label: `${structure.label} · ${tweak.label}`,
    structureHint: structure.structureHint,
    active: true,
    createdAt: Date.now(),
    // stats live on the parent template under stats.variantStats[variantId]
    // so they ride the same tracking pipeline as subjectLineStats etc.
  };
};

// =============================================================================
// DEFAULT VOICE-CLONE TEMPLATE FACTORY
//
// Mirrors the standard template shape where it makes sense (topic, type,
// stats path) and adds clone-specific fields under `cloneConfig`.
// =============================================================================

// The structure that every brand-new voice-clone template ships with as its
// first active variant. Picked to be the safe, balanced default — see the
// "Bonus Standard" entry in STRUCTURE_LIBRARY for the rationale. Users can
// remove it and add anything else; we only seed it on creation.
export const DEFAULT_VARIANT_STRUCTURE_ID = "bonusStandard";
export const DEFAULT_VARIANT_TWEAK_ID     = "natural";

export const createVoiceCloneTemplate = ({ type, topic }) => {
  const enhancements = INFO_ENHANCEMENTS.reduce((acc, s) => {
    acc[s.id] = s.defaultOn;
    return acc;
  }, {});

  // Seed one Bonus-Standard variant so the user has a working A/B baseline
  // the moment the editor opens. Falls back to an empty list if (for any
  // reason) the structure / tweak ids can't be resolved — defensive only;
  // both are guaranteed to exist at module load time.
  const defaultVariant = createVariant(
    DEFAULT_VARIANT_STRUCTURE_ID,
    DEFAULT_VARIANT_TWEAK_ID,
  );
  const seedVariants = defaultVariant ? [defaultVariant] : [];

  return {
    // Same identifiers as standard templates so the existing list / stats /
    // selection mechanics work without branching.
    topic: topic || "",
    type: type || "intro",
    isCustom: true,
    aiTagId: "",
    mode: TEMPLATE_MODES.VOICE_CLONE,

    // Clone-specific config — namespaced so it's clearly separate from the
    // standard-mode fields that the existing editor reads/writes.
    cloneConfig: {
      cloneId: "",            // ref → /World/{userId}/copyClones/{cloneId}

      // Language the generated emails are written in. Same string convention
      // as the Standard-mode pipeline ("English" / "German" / …) so the
      // existing backend can consume it directly.
      language: CAMPAIGN_LANGUAGE_DEFAULT,

      // Field-selection — what the AI may know about each recipient.
      // Reference SCHEMA only (keys / IDs); actual values are pulled per-contact
      // at generation time.
      fieldConfig: {
        enabledStandardKeys: DEFAULT_ENABLED_STANDARD_KEYS,
        // For custom fields the editor seeds this from the admin defaults
        // (those marked `useAsEmailContext: true`) the first time the template
        // is opened — no need to hardcode here.
        enabledCustomIds: null, // null = "not yet initialized, take admin default"
      },

      // Optional enhancements layered on top of the raw field data.
      enhancements,           // { webResearch, recipientHistory, senderContext }

      fixedFacts: [],         // string[] — LEGACY, kept for back-compat (old templates).
                              //   New templates use `extraRules` instead.
      campaignTaboos: [],     // string[] — additive on top of clone.taboos
      angles: [],             // string[] — LEGACY, kept for back-compat (old templates).
                              //   New templates use `extraRules` instead.
      extraRules: "",         // EXTRA RULES — free-text bucket where the user can
                              // pin additional must-follow rules / notes / hard
                              // facts the AI should respect on top of the brief.
                              // Lives in the Details tab.
      // Seeded with the Bonus-Standard variant so every new template has a
      // working baseline the moment it's opened. User can delete it.
      variants: seedVariants, // [{ id, structureId, lengthId, label, ..., active }]

      // ===== CAMPAIGN BRIEF — the three answers that define what the AI
      // pulls from for this campaign. We deliberately do NOT include the
      // recipient profile here — that's per-contact and we get it from the
      // World table at send time. =====
      references: "",         // REFS    — proof / references the AI may use
      uniqueAdvantage: "",    // SPECIAL — what makes us stand out
      painPoint: "",          // PAIN    — the recipient's pain we try to solve
      goal: "",               // WHY     — desired outcome of the campaign

      // Flexible info pools the user attaches to this template. Each addon
      // is { id, type, label, icon, items: [string] } and gets surfaced to
      // the AI as scoped material it may pull from. The catalog of types
      // lives in INFO_ADDON_PRESETS below; the user can also create custom
      // addons.
      infoAddons: [],

      // Legacy fields — kept for back-compat with templates created before
      // the brief refactor. New saves leave them blank.
      audienceOverride: "",
      audience: "",
      offer: "",

      smartSubject: true,     // true = clone writes per-recipient subject; false = use subjectLines list

      // Two adherence dials — independent control over how strictly the model
      // sticks to (a) the writer's voice and (b) the campaign brief. Voice
      // defaults to "mirror" (faithful clone) so out-of-the-box the subject +
      // body read like the writer wrote them. See VOICE_ADHERENCE_LEVELS /
      // BRIEF_ADHERENCE_LEVELS.
      voiceAdherence: VOICE_ADHERENCE_DEFAULT,
      briefAdherence: BRIEF_ADHERENCE_DEFAULT,

      // Creativity (sampling temperature) dial. Default keeps the historic
      // ~0.85 behavior; the user dials up to "Creative" / "Max" for more
      // variety. Surfaced in the editor only when voice adherence < mirror.
      creativity: CREATIVITY_DEFAULT,

      // Opt-in topic/industry adaptation. OFF = faithful style clone (default);
      // ON = the AI may drift from the trained style to fit a new topic
      // (the editor warns the user when they flip this on). Read by the backend
      // subject + body generators to decide faithful vs adaptive prompting.
      allowStyleAdaptation: STYLE_ADAPTATION_DEFAULT,

      // Where the email STRUCTURE comes from. 'twin' (default) copies the
      // writer's own architecture + length from the CopyTwin's Voice DNA; the
      // structure variants below become optional. 'custom' uses the configured
      // structure variants (curated library beats + A/B). See STRUCTURE_SOURCES.
      structureSource: STRUCTURE_SOURCE_DEFAULT,

      // Generation quality tier — neutral 4-step scale ("basic" → "best") that
      // the backend maps to engine + maxTokens + thinking config. Used by the
      // live preview today; will also drive real sends in Step 6+.
      qualityTier: QUALITY_TIER_DEFAULT,
    },

    // Stats schema is the SAME as standard mode + variantStats:
    //   stats.totalSent, stats.totalReplies
    //   stats.subjectLineStats[subjectLineId] = { sent, replies }
    //   stats.variantStats[variantId]        = { sent, replies }   <-- new
    // No initial values — backend populates lazily.
  };
};

// =============================================================================
// AUTO-SETUP MERGE — apply the one-click setup derived from a CopyTwin
// (deriveVoiceCloneTemplateSetup) onto a freshly created template.
//
// We start from createVoiceCloneTemplate (so every default + the seed variant
// is present) and overlay ONLY the fields the derivation actually produced.
// Empty/blank derived fields fall back to the factory default — the user can
// always refine in the editor. A small `autoSetup` provenance block records
// that this template was bootstrapped from a CopyTwin.
// =============================================================================
export const applyAutoSetupToCloneTemplate = (template, { cloneId, setup }) => {
  const base = template || {};
  const cc = base.cloneConfig || {};
  const brief = (setup && setup.brief) || {};
  const meta = (setup && setup.meta) || {};

  const nonEmpty = (v) => typeof v === "string" && v.trim().length > 0;
  const overlay = (key, value) => (nonEmpty(value) ? { [key]: value.trim() } : {});

  return {
    ...base,
    cloneConfig: {
      ...cc,
      cloneId: cloneId || cc.cloneId || "",
      ...overlay("language", setup && setup.language),
      paragraphRhythm: (setup && setup.paragraphRhythm) || cc.paragraphRhythm || "auto",
      structureSource: (setup && setup.structureSource) || cc.structureSource || STRUCTURE_SOURCE_DEFAULT,
      ...overlay("goal", brief.goal),
      ...overlay("painPoint", brief.painPoint),
      ...overlay("uniqueAdvantage", brief.uniqueAdvantage),
      ...overlay("references", brief.references),
      autoSetup: {
        source: "copytwin",
        cloneId: cloneId || cc.cloneId || "",
        appliedAt: Date.now(),
        confidence: meta.confidence || "",
        modelUsed: meta.modelUsed || "",
        version: (setup && setup.version) || 1,
      },
    },
  };
};

// =============================================================================
// MODE DETECTION HELPER — single source of truth for "is this a clone template?"
// =============================================================================

export const isVoiceCloneTemplate = (template) =>
  template?.mode === TEMPLATE_MODES.VOICE_CLONE;

// =============================================================================
// CLONE × TEMPLATE COMPATIBILITY — warnings when a template setting fights
// the clone's natural DNA.
//
// Why this exists:
//   The user can pick any structure, any length tweak, any paragraph rhythm,
//   any greeting/sign-off override they want — but some combinations pull
//   the AI in opposite directions from the clone's actual writing patterns.
//   E.g. clone naturally writes ~80-word emails but the variant targets
//   ~300; clone signs every email "Cheers" but the template forces "Mit
//   freundlichen Grüßen". When that happens, the AI either compromises
//   (voice drift) or ignores the template (rule confusion). Either way the
//   user gets output that "doesn't sound like me" — exactly what we want
//   to avoid.
//
// The compatibility checks below detect those conflicts BEFORE generation.
// We surface them as soft yellow warnings in the editor so the user can
// either adjust or knowingly accept the trade-off. They are NEVER hard
// blockers — the user always has the final say (some campaigns DO need to
// break voice habits, e.g. a formal compliance email from a normally-
// casual writer).
//
// Conventions:
//   - severity: 'high' = strong DNA signal contradicted (e.g. 11/12 sign-offs)
//                'medium' = soft signal, worth flagging
//                'low'    = informational only
//   - title:    short label for the chip
//   - message:  one-line explanation the user sees on hover/inline
//   - hint:     concrete action they can take to fix it
//   - field:    semantic id of which setting is conflicting (for analytics)
// =============================================================================

// Min count required before we treat a character marker (greeting word,
// sign-off word) as a "strong" signal worth warning about overrides for.
// Both an absolute floor and a relative threshold — a 1/1 sign-off
// shouldn't trigger warnings (no real signal yet), but 8/10 absolutely
// should. 0.6 is the same threshold the prompt builder uses to decide
// when to escalate "occasional" → "frequent" framing for catchphrases.
const STRONG_MARKER_MIN_COUNT = 4;
const STRONG_MARKER_MIN_RATIO = 0.6;

const isStrongMarker = (marker) => {
  if (!marker) return false;
  const count = Number(marker.count) || 0;
  const total = Number(marker.total) || 0;
  if (count < STRONG_MARKER_MIN_COUNT) return false;
  if (total > 0 && count / total < STRONG_MARKER_MIN_RATIO) return false;
  return true;
};

// Strip a greeting/sign-off TEMPLATE down to the parts that aren't variable
// substitutions ("Hallo {{firstName}}, ich..." → "Hallo , ich..."). Used to
// scan the user-configured opener for the clone's natural greeting word
// (or any of its alternates) — if it's already there, no conflict.
const stripPlaceholders = (s) =>
  String(s || '')
    .replace(/\{\{[^}]+\}\}/g, ' ')
    .replace(/[,.!?;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const includesWord = (haystack, needle) => {
  if (!needle) return false;
  const h = stripPlaceholders(haystack);
  const n = String(needle).trim().toLowerCase();
  if (!h || !n) return false;
  const re = new RegExp(`(^|\\s)${n.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}(\\s|$)`, 'i');
  return re.test(h);
};

// Greeting-word presence inside the configured greeting template, including
// any DNA-known alternates. Lets the user override the form ("Hi {{firstName}}!"
// vs "Hi {{firstName}},") without triggering a false-positive conflict.
const greetingTemplateMatchesDnaWord = (template, greetingChar) => {
  if (!template || !greetingChar) return false;
  if (includesWord(template, greetingChar.word)) return true;
  for (const alt of greetingChar.alternates || []) {
    if (includesWord(template, alt)) return true;
  }
  return false;
};

// =============================================================================
// PER-VARIANT CONFLICTS
// =============================================================================
// Warnings that depend on the variant's own settings (structure + length
// tweak + customWords). Called once per variant card.
// =============================================================================

export const computeVariantConflicts = (clone, variant, cloneAvgWords) => {
  const conflicts = [];
  if (!clone || !clone.voiceDNA || !variant) return conflicts;

  const structureId = variant.structureId;
  const tweakId = resolveLengthTweakId(variant.lengthTweakId || variant.lengthId);
  const structure = STRUCTURE_BY_ID[structureId];
  if (!structure) return conflicts;

  // ---------- 1. Length vs clone's natural rhythm ----------
  // Compare the variant's TARGET word count against the clone's measured
  // avg email length. If the gap is large the AI will either pad (filler,
  // diluted voice) or compress (drop signature beats). 60% gap is the
  // threshold where we've seen actual voice drift in production output.
  if (cloneAvgWords && cloneAvgWords > 0) {
    const target = computeTargetWords(
      structure,
      tweakId,
      cloneAvgWords,
      tweakId === "custom" ? variant.customWords : undefined,
    );
    if (target > 0) {
      const ratio = target / cloneAvgWords;
      if (ratio >= 1.6) {
        conflicts.push({
          id: 'length-too-long',
          severity: ratio >= 2.2 ? 'high' : 'medium',
          field: 'length',
          title: 'Long for this clone',
          message:
            `Your clone writes ~${cloneAvgWords} words on average, this variant targets ~${target}. ` +
            `Stretching the voice that much usually adds filler and dilutes the signature.`,
          hint: 'Try "Mirror clone" or "Shorter" — or pick a structure with a higher natural length.',
        });
      } else if (ratio <= 0.55) {
        conflicts.push({
          id: 'length-too-short',
          severity: ratio <= 0.4 ? 'high' : 'medium',
          field: 'length',
          title: 'Short for this clone',
          message:
            `Your clone writes ~${cloneAvgWords} words on average, this variant targets ~${target}. ` +
            `Cropping that much usually forces out the beats your clone leans on (proof, story, context).`,
          hint: 'Try "Mirror clone" or "Longer" — or pick a tighter structure built for short copy.',
        });
      }
    }
  }

  return conflicts;
};

// =============================================================================
// TEMPLATE-LEVEL CONFLICTS
// =============================================================================
// Warnings on settings that live on cloneConfig (not on a specific variant)
// vs the clone's DNA. Rendered once at the top of the editor's Voice tab so
// the user sees them in context of the controls that caused them.
// =============================================================================

export const computeTemplateConflicts = (clone, cloneConfig) => {
  const conflicts = [];
  if (!clone || !clone.voiceDNA || !cloneConfig) return conflicts;
  const dna = clone.voiceDNA;
  const character = dna.character || {};

  // ---------- 2. Paragraph rhythm override vs DNA rhythm ----------
  // The template can pin a paragraph rhythm ('tight' / 'standard' / 'airy')
  // that overrides voiceDNA.rhythm.paragraphStyle. Direct contradiction =
  // the model has to pick a side. We warn so the user knows the cost.
  const configRhythm = cloneConfig.paragraphRhythm;
  const dnaRhythm = dna.rhythm?.paragraphStyle;
  if (
    configRhythm &&
    configRhythm !== 'auto' &&
    dnaRhythm &&
    configRhythm !== dnaRhythm
  ) {
    // Only warn when the contrast is meaningful (tight vs airy, airy vs
    // tight). "tight vs standard" or "standard vs airy" are adjacent and
    // rarely cause noticeable voice drift.
    const farPairs = new Set(['tight|airy', 'airy|tight']);
    const key = `${configRhythm}|${dnaRhythm}`;
    if (farPairs.has(key)) {
      conflicts.push({
        id: 'paragraph-rhythm-conflict',
        severity: 'medium',
        field: 'paragraphRhythm',
        title: 'Paragraph rhythm fights the clone',
        message:
          `Your clone naturally writes in ${dnaRhythm} paragraphs, this template forces ${configRhythm}. ` +
          `The AI will have to fight the DNA to comply.`,
        hint: 'Set paragraph rhythm to "Auto" to let the clone\'s own rhythm decide.',
      });
    }
  }

  // ---------- 3. Greeting template override vs DNA greeting word ----------
  // If the user explicitly switched greeting to CONFIGURED and pinned a
  // specific opener that DOESN'T contain the clone's natural greeting word
  // (or any of its alternates), the email will open with a non-clone
  // greeting even though the clone has a strong identity marker for it.
  const greeting = cloneConfig.greeting;
  if (greeting && greeting.mode === GREETING_MODES.CONFIGURED) {
    const dnaGreeting = character.greeting;
    if (isStrongMarker(dnaGreeting)) {
      const opener = `${greeting.withFirstName || ''} ${greeting.withoutFirstName || ''}`;
      if (!greetingTemplateMatchesDnaWord(opener, dnaGreeting)) {
        conflicts.push({
          id: 'greeting-override-conflict',
          severity: 'high',
          field: 'greeting',
          title: 'Greeting overrides the clone',
          message:
            `Your clone opens with "${dnaGreeting.word}" in ${dnaGreeting.count}/${dnaGreeting.total} samples — ` +
            `but the configured greeting doesn't use it. ` +
            `This is one of the loudest identity markers; overriding it costs voice authenticity.`,
          hint: `Either include "${dnaGreeting.word}" in the greeting template, or switch greeting mode back to "Auto".`,
        });
      }
    }
  }

  // ---------- 4. Sign-off identity override vs DNA sign-off word ----------
  // The sign-off override changes WHO the email is signed as (different
  // sender name/role), not the closing word itself. But when the
  // workspace's effective sender name diverges from what the DNA expects,
  // the closing line can feel mismatched. We flag it only as low severity
  // since the override is usually intentional (workspace personas).
  const signOffIdentity = cloneConfig.signOffIdentity;
  const dnaSignOff = character.signOff;
  if (
    signOffIdentity &&
    signOffIdentity.mode === SIGN_OFF_IDENTITY_MODES.CUSTOM &&
    isStrongMarker(dnaSignOff) &&
    !signOffIdentity.name
  ) {
    conflicts.push({
      id: 'signoff-identity-empty',
      severity: 'low',
      field: 'signOffIdentity',
      title: 'Custom sign-off identity is empty',
      message:
        `Sign-off mode is "Custom" but no name is set. The clone usually closes as "${dnaSignOff.word}" — ` +
        `falling back to the workspace sender, which may not match the clone's voice.`,
      hint: 'Fill in the sign-off name, or switch back to "Sender".',
    });
  }

  // ---------- 5. Smart subject off + no subject lines defined? ----------
  // Generation will fall back to template-name-as-subject. Warn so users
  // notice before sending.
  if (
    cloneConfig.smartSubject === false &&
    (!Array.isArray(cloneConfig.subjectLines) ||
      cloneConfig.subjectLines.length === 0)
  ) {
    conflicts.push({
      id: 'subject-fallback',
      severity: 'medium',
      field: 'smartSubject',
      title: 'No subject lines',
      message:
        'Smart subject is OFF and no manual subject lines are defined — ' +
        'recipients will see the template name as the subject.',
      hint: 'Turn smart subject ON, or add at least one manual subject line.',
    });
  }

  // ---------- 7. Voice adherence dialed to "Free" ----------
  // The whole point of a Voice-Clone template is that it sounds like the
  // clone. "Free" tells the model the voice DNA is soft inspiration only
  // and to prioritise generic recipient-fit over the writer's patterns —
  // which is exactly the "the output stopped sounding like me / my CTA is
  // gone" failure mode. Default is "mirror" (faithful clone), so this only
  // fires when the user actively dialed it down — a "did you mean that?" guard.
  const voiceAdherence = cloneConfig.voiceAdherence || VOICE_ADHERENCE_DEFAULT;
  if (voiceAdherence === 'free') {
    conflicts.push({
      id: 'voice-adherence-free',
      severity: 'medium',
      field: 'voiceAdherence',
      title: 'Voice adherence is set to Free',
      message:
        'On "Free" the AI treats your CopyTwin as loose inspiration only and ' +
        'prioritises generic recipient-fit over your style — including your ' +
        "natural CTA. That's a common reason output stops sounding like you.",
      hint: 'Set Voice adherence to "Balanced" (or higher) unless you deliberately want a generic voice for this campaign.',
    });
  }

  // ---------- 8. Brief adherence dialed to "Strict" + a Goal set ----------
  // "Strict" forces EVERY brief item — including the campaign Goal and every
  // angle — to appear in the email. The Goal materialises in the CTA beat, so
  // a Goal that differs from the writer's natural ask will override the
  // clone's CTA style. This is the exact mismatch behind "I fed similar CTAs
  // but the generated CTA was completely different". Only fires when a Goal is
  // actually set (no Goal → nothing to override).
  const briefAdherence = cloneConfig.briefAdherence || BRIEF_ADHERENCE_DEFAULT;
  const hasGoal = String(cloneConfig.goal || '').trim().length > 0;
  if (briefAdherence === 'strict' && hasGoal) {
    conflicts.push({
      id: 'brief-adherence-strict-goal',
      severity: 'medium',
      field: 'briefAdherence',
      title: 'Strict brief can override the clone',
      message:
        'Brief adherence is "Strict", so the campaign Goal (and every angle) ' +
        'MUST appear — and the Goal drives the CTA. If the Goal asks for ' +
        "something different from your clone's natural close, it wins and the " +
        "clone's CTA style is overridden.",
      hint: 'Set Brief adherence to "Balanced", or double-check the Goal text matches the ask you actually want.',
    });
  }

  // ---------- 9. Goal drives the CTA (informational) ----------
  // Even at default adherence, the campaign Goal is the single biggest driver
  // of the generated CTA, while the clone's sample CTAs are used for STYLE
  // only (anonymised, never copied verbatim). Users repeatedly expect the
  // generated CTA to match their sample CTAs and are surprised when it
  // follows the Goal instead. We surface a quiet, low-severity note — but
  // ONLY when the clone actually has a learned CTA style worth mentioning
  // (a populated cta phrase-bank bucket) AND a Goal is set, so it never
  // becomes generic noise. Suppressed when #8 already covers the same ground.
  const ctaBucket = Array.isArray(dna.phraseBank?.cta) ? dna.phraseBank.cta : [];
  if (
    hasGoal &&
    ctaBucket.length >= 2 &&
    !(briefAdherence === 'strict')
  ) {
    conflicts.push({
      id: 'goal-drives-cta',
      severity: 'low',
      field: 'goal',
      title: 'Your Goal shapes the CTA',
      message:
        `The campaign Goal is what the email's CTA is built around. Your clone ` +
        `has a learned CTA style (${ctaBucket.length} example${ctaBucket.length === 1 ? '' : 's'}), ` +
        `but those lend phrasing/rhythm only — the actual ask comes from the Goal.`,
      hint: 'Make sure the Goal describes the exact next step you want — that, not the sample CTAs, decides what the email asks for.',
    });
  }

  // ---------- 10. Description / identity story empty ----------
  // The identity story is the writer's own voice description and is now
  // the top-priority anchor in the prompt. When it's empty the clone has
  // to lean entirely on auto-extracted DNA — which is less precise. We
  // flag this once so the user knows to fill it in.
  const storyLen = String(clone.identity?.story || '').trim().length;
  if (storyLen < 60) {
    conflicts.push({
      id: 'identity-story-thin',
      severity: storyLen === 0 ? 'medium' : 'low',
      field: 'identity',
      title: storyLen === 0 ? 'Clone description is empty' : 'Clone description is short',
      message:
        storyLen === 0
          ? 'Your clone has no voice description. The AI is leaning 100% on auto-extracted DNA, which misses intent (who you write to, what register, what angle).'
          : 'Your clone description is very short. The AI uses this as the top-priority frame — a few extra sentences about audience and register would noticeably sharpen output.',
      hint: 'Open the clone and add 2–3 sentences about who you write to and how you want to come across.',
    });
  }

  return conflicts;
};
